/**
 * Room codes look like `abc-defg-hij`: 10 lowercase letters, about 47 bits of
 * entropy (26^10), grouped 3-4-3 so they are easy to read aloud.
 */
const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';
export const ROOM_CODE_PATTERN = /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/;

/** `randomBytes` must return cryptographically secure bytes. */
export function generateRoomCode(randomBytes: (n: number) => Uint8Array): string {
  const letters: string[] = [];
  // Rejection sampling keeps the distribution uniform: 234 is the largest
  // multiple of 26 that fits in a byte.
  while (letters.length < 10) {
    for (const byte of randomBytes(16)) {
      if (byte < 234 && letters.length < 10) letters.push(ALPHABET[byte % 26]!);
    }
  }
  const s = letters.join('');
  return `${s.slice(0, 3)}-${s.slice(3, 7)}-${s.slice(7)}`;
}

export function isRoomCode(value: string): boolean {
  return ROOM_CODE_PATTERN.test(value);
}

/**
 * Accepts what people actually type or paste: a full meeting link, a code
 * with spaces or capitals, or ten letters without dashes. Returns null when
 * it cannot be a room code.
 */
export function normalizeRoomCode(input: string): string | null {
  let value = input.trim();
  const fromLink = /\/m\/([^/?#\s]+)/.exec(value);
  if (fromLink) value = fromLink[1]!;
  const letters = value.toLowerCase().replace(/[\s-]+/g, '');
  if (!/^[a-z]{10}$/.test(letters)) return null;
  return `${letters.slice(0, 3)}-${letters.slice(3, 7)}-${letters.slice(7)}`;
}

export function meetingLink(origin: string, code: string): string {
  return `${origin.replace(/\/+$/, '')}/m/${code}`;
}
